import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PackageSearch, Search, ArrowRight, ShieldCheck, MessageCircle } from "lucide-react";

import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { listOrders } from "../lib/api";
import { formatPrice } from "../lib/filters";
import { WHATSAPP_URL } from "../lib/site";

const STATUS_META = {
  SUCCESS: { label: "Confirmed", cls: "bg-stone text-charcoal" },
  FAILED: { label: "Failed", cls: "bg-stone text-taupe" },
  PENDING: { label: "Pending", cls: "bg-stone text-charcoal" },
  CREATED: { label: "Received", cls: "bg-stone text-charcoal" },
  INITIATED: { label: "Processing", cls: "bg-stone text-charcoal" },
};

const statusMeta = (status) =>
  STATUS_META[status] ?? { label: status ?? "Processing", cls: "bg-stone text-charcoal" };

const inputCls =
  "w-full rounded-sm border border-beige bg-ivory px-4 py-3 text-sm text-charcoal placeholder:text-taupe focus:outline-none focus:border-charcoal transition-colors";

const formatDate = (ts) => {
  if (!ts) return "";
  const d = new Date(String(ts).replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return String(ts);
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

export default function OrderHistory() {
  const [phone, setPhone] = useState("");
  const [error, setError] = useState(null);
  const [state, setState] = useState("idle"); // idle | loading | done
  const [orders, setOrders] = useState([]);
  const [searched, setSearched] = useState(false);

  const lookup = async (e) => {
    e?.preventDefault();
    const digits = phone.replace(/\D/g, "");
    if (!/^\d{10}$/.test(digits)) {
      setError("Enter a valid 10-digit phone number");
      return;
    }
    setError(null);
    setState("loading");
    setSearched(false);
    try {
      const res = await listOrders(digits);
      setOrders(res.orders ?? []);
      setSearched(true);
    } catch {
      setOrders([]);
      setSearched(true);
      setError("We couldn't fetch your orders. Please try again.");
    } finally {
      setState("done");
    }
  };

  const hasOrders = orders.length > 0;

  return (
    <div data-testid="order-history" className="bg-ivory min-h-screen">
      <Header />

      <main className="pt-28 md:pt-32 pb-28 lg:pb-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex flex-col items-center text-center">
              <span className="grid place-items-center h-20 w-20 rounded-full bg-stone text-charcoal">
                <PackageSearch size={30} strokeWidth={1.3} />
              </span>
              <h1 className="font-serif font-light text-charcoal text-4xl md:text-5xl mt-8 tracking-tight">
                Track your orders
              </h1>
              <p className="mt-4 max-w-md text-charcoal/65 leading-relaxed">
                Enter the phone number you used at checkout to see all your BathLane orders and
                their status.
              </p>
            </div>

            <form
              onSubmit={lookup}
              data-testid="order-lookup-form"
              className="mt-10 flex flex-col sm:flex-row gap-3"
            >
              <input
                data-testid="order-lookup-phone"
                className={inputCls}
                placeholder="10-digit mobile number"
                inputMode="numeric"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
              <button
                type="submit"
                disabled={state === "loading"}
                data-testid="order-lookup-submit"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-charcoal text-ivory px-8 py-3.5 text-sm font-medium btn-fill hover:text-charcoal transition-colors duration-500 disabled:opacity-50 shrink-0"
              >
                {state === "loading" ? "Looking up…" : "Find my orders"}
                {state !== "loading" && <Search size={15} strokeWidth={1.5} />}
              </button>
            </form>
            {error && !hasOrders && (
              <p data-testid="order-lookup-error" className="mt-3 text-xs text-[#a33]">
                {error}
              </p>
            )}
          </motion.div>

          {searched && !hasOrders && (
            <div data-testid="order-history-empty" className="mt-16 text-center border border-beige rounded-sm px-6 py-14">
              <h2 className="font-serif font-light text-charcoal text-3xl">No orders found</h2>
              <p className="mt-3 text-sm text-charcoal/65 leading-relaxed max-w-sm mx-auto">
                We couldn't find any orders for this number. If you believe this is a mistake,
                reach out on WhatsApp and we'll help you right away.
              </p>
              <a
                href={WHATSAPP_URL("Hi BathLane, I can't find my order on the website. Can you help?")}
                target="_blank"
                rel="noreferrer"
                className="mt-7 inline-flex items-center gap-2 rounded-full border border-charcoal px-7 py-3.5 text-sm font-medium text-charcoal hover:bg-stone/60 transition-colors duration-300"
              >
                <MessageCircle size={15} strokeWidth={1.5} /> Ask on WhatsApp
              </a>
            </div>
          )}

          {hasOrders && (
            <div className="mt-14 space-y-5" data-testid="order-list">
              <p className="eyebrow text-taupe">
                {orders.length} {orders.length === 1 ? "order" : "orders"}
              </p>
              {orders.map((o) => {
                const meta = statusMeta(o.status);
                return (
                  <Link
                    key={o.order_id}
                    to={`/orders/${o.order_id}`}
                    data-testid="order-card"
                    className="block border border-beige rounded-sm p-6 md:p-7 hover:border-charcoal transition-colors duration-300"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <p className="font-serif text-xl text-charcoal truncate">{o.order_id}</p>
                        <p className="mt-1 text-xs text-taupe">
                          {formatDate(o.created_at)}
                          {o.payment_type === "PREPAID" ? " · Prepaid" : " · Cash on delivery"}
                        </p>
                      </div>
                      <span
                        data-testid="order-status"
                        className={`shrink-0 rounded-full px-3.5 py-1.5 text-xs font-medium ${meta.cls}`}
                      >
                        {meta.label}
                      </span>
                    </div>

                    <div className="mt-5 flex items-center gap-4">
                      <div className="flex -space-x-3">
                        {(o.items ?? []).slice(0, 3).map((it, idx) => (
                          <span
                            key={idx}
                            className="grid place-items-center h-14 w-14 rounded-full border border-beige bg-stone overflow-hidden"
                          >
                            {it.image ? (
                              <img src={it.image} alt={it.name ?? ""} className="h-full w-full object-cover" />
                            ) : (
                              <span className="text-[10px] text-taupe px-1 text-center leading-tight">
                                {(it.name ?? "Item").slice(0, 18)}
                              </span>
                            )}
                          </span>
                        ))}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm text-charcoal/70 truncate">
                          {o.item_count > 0
                            ? `${o.item_count} ${o.item_count === 1 ? "item" : "items"}`
                            : "Order details coming soon"}
                        </p>
                        <p className="mt-0.5 text-sm text-charcoal/55 truncate">
                          {(o.items ?? []).map((it) => it.name).filter(Boolean).join(" · ")}
                        </p>
                      </div>
                      <span className="ml-auto font-serif text-lg text-charcoal whitespace-nowrap">
                        {formatPrice(Number(o.total) || 0)}
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}

          {hasOrders && (
            <div className="mt-8 flex items-center gap-2.5 border border-beige rounded-sm px-4 py-3.5">
              <ShieldCheck size={15} strokeWidth={1.4} className="text-charcoal shrink-0" />
              <span className="text-xs text-charcoal/75">
                Need help with an order?{" "}
                <a
                  href={WHATSAPP_URL("Hi BathLane, I need help with my order.")}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-charcoal font-medium link-sweep"
                >
                  <MessageCircle size={13} strokeWidth={1.5} /> Chat with us
                </a>
              </span>
            </div>
          )}

          <div className="mt-12 text-center">
            <Link
              to="/vanities"
              data-testid="order-history-shop"
              className="inline-flex items-center gap-2 rounded-full bg-charcoal text-ivory px-8 py-4 text-sm font-medium btn-fill hover:text-charcoal transition-colors duration-500"
            >
              Shop Vanities <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
